import React, { useContext } from 'react';

import { format } from 'date-fns';

import { FadeInComponent } from '@/components/FadeInComponent';
import { Highlight } from '@/components/Highlight';
import { Account } from '@/core/api/collections/currentAccount';
import { AuthContext } from '@/core/auth/AuthContext';
import { StickyHeader } from '@/core/users/StickyHeader';
import { UserInviteContext } from '@/core/users/UserInvite/UserInviteContext';
import { UserInviteHeaderInputFields } from '@/core/users/UserInvite/UserInviteHeaderInputFields';
import { UserInviteSubmitButton } from '@/core/users/UserInvite/UserInviteSubmitButton';
import { ReactComponent as WarningIcon } from '@/icons/warning.svg';
import theme from '@/theme';

export function UserInviteHeader () {
  const { hasCoreAccess, currentAccount } = useContext(AuthContext);
  const { billingRate } = useContext(UserInviteContext);

  return (
    <StickyHeader renderNotification={billingRate && (
      <FadeInComponent key="billing-header" className="col-span-full">
        <UserInviteBillingHeader account={currentAccount!} rate={billingRate} />
      </FadeInComponent>
    )}>
      <div className="col-span-4">
        <div className="pr-8 font-serif text-5xl font-bold text-blue-900 truncate">
          Invite
          <Highlight
            className="mx-3"
            variant="macheteInverse"
            text="team"
            color={theme.palette.blue['300']}
          />
          member
        </div>
        <div className="mt-4">
          Your teammates will get an email that gives them access to your UserVoice account.
        </div>
      </div>
      <div className="col-span-2">
        <div className="flex justify-end">
          <UserInviteSubmitButton />
        </div>
      </div>

      <UserInviteHeaderInputFields />

      <div className="pb-3 text-lg font-medium text-blue-900 col-span-full">
        Assign access {hasCoreAccess && 'and teams'}
      </div>
    </StickyHeader>
  );
}

type UserInviteBillingHeaderProps = {
  account: Account;
  rate?: number;
};
const UserInviteBillingHeader = ({ account, rate }: UserInviteBillingHeaderProps) => {
  if (!account.paymentDate || !account.billingInterval || !rate) {
    return null;
  }

  const newRate = (rate / 100).toFixed(2);
  let paymentDate = new Date(account.paymentDate);
  const dateStr = format(paymentDate, 'MM-dd-yyyy');
  return (
    <div className="flex text-sm bg-blue-200 rounded p-2.5 col-span-full gap-0">
      <WarningIcon className="w-5 h-5 mb-1 mr-2 text-blue-600" />
      Based on the license you selected, your new {account.billingInterval} rate will be ${newRate} (next payment of {dateStr}).
    </div>
  );
};
